import {createStore, StoreApi, useStore, UseBoundStore} from 'zustand';
import {BootstrapData} from '@ui/bootstrap-data/bootstrap-data';

export interface BootstrapDataState {
  data: BootstrapData;
  setData: (data: BootstrapData | string) => void;
  mergeData: (data: Partial<BootstrapData>) => void;
}

// Not: normalde `create()` kullanılırdı; ancak SSR (Konu 1) için `createStore` +
// `useStore` ile açık kurup `api` referansını elde ediyoruz (aşağıdaki
// getServerState yaması `api` üzerinde OLMALI — `create`'in döndürdüğü bound
// hook'a yazmak işe yaramaz, çünkü useStore içeride `api`yi okur). Bunun dışında
// davranış `create` ile birebir aynıdır.
export function createBootstrapDataStore(
  initialData?: BootstrapData | string | null,
): StoreApi<BootstrapDataState> {
  const api = createStore<BootstrapDataState>()(set => ({
    // set bootstrap data that was provided with initial request from backend
    data: initialData ? decodeBootstrapData(initialData) : null!,
    setData: data => {
      set({data: decodeBootstrapData(data)});
    },
    mergeData: (partial: Partial<BootstrapData>) => {
      set(state => ({data: {...state.data, ...partial}}));
    },
  }));

  // Sunucuda kurulan İSTEK-BAŞI store'lar kendi canlı durumunu döndürsün
  // (gerekçe için aşağıdaki getServerState notuna bakın).
  if (typeof window === 'undefined') {
    (api as unknown as {getServerState?: () => BootstrapDataState}).getServerState =
      api.getState;
  }

  return api;
}

const bootstrapApi = createBootstrapDataStore(
  typeof window !== 'undefined' ? window.bootstrapData : null,
);

// SSR YARIŞ DURUMU: bu modül süreç ömürlüdür, bu yüzden eşzamanlı render'lar tek
// store'u paylaşırsa son yazan isteğin verisi uçuştaki TÜM render'ları ele geçirir
// (ölçüm: 12 eşzamanlı istekte %72, üretimdeki 3'lük kapakta %33 yanlış dil).
// Çözüm: Node girişi (server-entry) istek başına store kurar ve burayı bir çözücü
// ile besler. `async_hooks` KASITLI olarak burada değil — bu dosya tarayıcı
// paketine de giriyor. Çözücü tarayıcıda hiç kurulmaz → davranış birebir aynı.
type RequestStateResolver = () => BootstrapDataState | undefined;
let requestStateResolver: RequestStateResolver | null = null;

export function setRequestStateResolver(
  resolver: RequestStateResolver | null,
): void {
  requestStateResolver = resolver;
}

function activeState(): BootstrapDataState {
  return requestStateResolver?.() ?? bootstrapApi.getState();
}

// SSR (Konu 1): React 18 renderToPipeableStream, zustand useStore'un SUNUCU
// snapshot'ı için `api.getServerState || api.getInitialState` kullanır
// (zustand ≥4.5). getInitialState store yaratılış anındaki durumu döndürür →
// Node'da (window yok) bu `data: null`'dır; yani SSR sırasında yazılan istek-başı
// bootstrap verisi GÖRÜNMEZ (ThemeProvider/useAuth/useSettings null okur → SSR
// crash). getServerState'i aktif (istek-başı) duruma bağlayarak sunucu render'ı
// KENDİ isteğinin verisini okur. YALNIZ sunucuda uygulanır → tarayıcı davranışı
// birebir korunur (client hydration zaten window.bootstrapData'dan dolu initial
// state okur).
if (typeof window === 'undefined') {
  (
    bootstrapApi as unknown as {getServerState?: () => BootstrapDataState}
  ).getServerState = activeState;
}

export const useBootstrapDataStore = ((
  selector?: (state: BootstrapDataState) => unknown,
  equalityFn?: (a: unknown, b: unknown) => boolean,
) => useStore(bootstrapApi, selector as never, equalityFn as never)) as UseBoundStore<
  StoreApi<BootstrapDataState>
>;
Object.assign(useBootstrapDataStore, bootstrapApi);

// React ağacının dışından yapılan okumalar da (axios interceptor, URL yardımcıları)
// aktif isteğin durumuna gitsin; tarayıcıda çözücü null olduğu için doğrudan
// global store'a düşer.
export const getBootstrapData = () => activeState().data;
export const setBootstrapData = (data: BootstrapData | string) =>
  activeState().setData(data);
export const mergeBootstrapData = (data: Partial<BootstrapData>) =>
  activeState().mergeData(data);

export function decodeBootstrapData(
  data: string | BootstrapData,
): BootstrapData {
  return typeof data === 'string' ? JSON.parse(data) : data;
}
